import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { clipTopicName } from '../../utils/topicUtils';
import './HeatmapPanel.css';

/**
 * HeatmapPanel – Industry × Topics cross-tabulation heatmap.
 *
 * Props:
 *   data        – { rows: string[], columns: string[], matrix: number[][], maxValue: number }
 *   themeColors – from colorConstants.js
 *   theme       – 'autumn' | 'dark'
 */
function HeatmapPanel({ data, themeColors, theme, onCellClick, axes, axisOptions, onAxisChange, recordLabel = 'Record' }) {
  const recordLabelPlural = /s$/i.test(recordLabel) ? recordLabel : `${recordLabel}s`;
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const tooltipRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [hoveredCell, setHoveredCell] = useState(null);
  const [axisMenuOpen, setAxisMenuOpen] = useState(false);
  const axisMenuRef = useRef(null);

  // Close axis menu on outside click
  useEffect(() => {
    if (!axisMenuOpen) return;
    const onDocClick = (e) => {
      if (axisMenuRef.current && !axisMenuRef.current.contains(e.target)) {
        setAxisMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [axisMenuOpen]);

  // ── Colour helpers ────────────────────────────────────────────────────────
  const isDark = theme === 'dark';

  // Build a gradient function based on the theme (YlOrRd-like warm ramp)
  const interpolateColor = useCallback((value, max) => {
    if (max === 0 || value === 0) {
      return isDark ? 'rgba(40,40,40,0.5)' : 'rgba(245,245,245,0.8)';
    }
    const t = Math.min(value / max, 1);

    // Warm ramp: light yellow → orange → deep red/teal
    const r0 = isDark ? 50 : 255, g0 = isDark ? 50 : 255, b0 = isDark ? 60 : 230;
    const rMid = isDark ? 255 : 230, gMid = isDark ? 160 : 140, bMid = isDark ? 40 : 50;
    const r1 = isDark ? 200 : 175, g1 = isDark ? 50 : 60, b1 = isDark ? 80 : 55;

    let r, g, b;
    if (t < 0.5) {
      const s = t * 2;
      r = Math.round(r0 + (rMid - r0) * s);
      g = Math.round(g0 + (gMid - g0) * s);
      b = Math.round(b0 + (bMid - b0) * s);
    } else {
      const s = (t - 0.5) * 2;
      r = Math.round(rMid + (r1 - rMid) * s);
      g = Math.round(gMid + (g1 - gMid) * s);
      b = Math.round(bMid + (b1 - bMid) * s);
    }
    return `rgb(${r},${g},${b})`;
  }, [isDark]);

  // ── Layout constants ──────────────────────────────────────────────────────
  const layout = useMemo(() => {
    if (!data || !data.rows || !data.columns) return null;

    const rowCount = data.rows.length;
    const colCount = data.columns.length;
    if (rowCount === 0 || colCount === 0) return null;

    // Calculate label widths: use longest label as proxy (clipped, as drawn)
    const longestRow = Math.max(...data.rows.map(r => clipTopicName(r).length), 8);
    // Use clipped column labels for sizing
    const longestCol = Math.max(...data.columns.map(c => clipTopicName(c).length), 8);
    const charWidth = 6.5;

    const leftMargin = Math.min(longestRow * charWidth + 16, 220);
    // For -60° rotation the vertical projection is charWidth × sin(60°) ≈ 5.6 per char
    const bottomMargin = Math.round(longestCol * 5.6 + 20);
    const topMargin = 48;
    const rightMargin = 50; // space for colour bar

    const availW = dimensions.width - leftMargin - rightMargin;
    const availH = dimensions.height - topMargin - bottomMargin;

    // Cell size: fill available space.
    // Dynamic caps based on container size – allow bigger cells when expanded.
    const maxCellW = dimensions.height > 500 ? 64 : 42;
    const maxCellH = dimensions.height > 500 ? 56 : 36;
    const cellW = Math.max(24, Math.min(maxCellW, Math.floor(availW / colCount)));
    const cellH = Math.max(20, Math.min(maxCellH, Math.floor(availH / rowCount)));

    // Extra space for totals row/column
    const totalGap = 3;
    const totalColW = cellW;
    const totalRowH = cellH;

    // Canvas size = exactly what the content needs. Not capped at the container:
    // when it exceeds the viewport the panel becomes scrollable.
    const canvasW = leftMargin + colCount * cellW + totalGap + totalColW + rightMargin;
    const canvasH = topMargin + rowCount * cellH + totalGap + totalRowH + bottomMargin;

    return { leftMargin, bottomMargin, topMargin, rightMargin, cellW, cellH, rowCount, colCount, canvasW, canvasH, totalGap, totalColW, totalRowH };
  }, [data, dimensions]);

  // ── Compute row / column totals ────────────────────────────────────────────
  const totals = useMemo(() => {
    if (!data || !data.matrix || !data.matrix.length) return null;
    const rowTotals = data.matrix.map(row => row.reduce((a, b) => a + b, 0));
    const colTotals = data.columns.map((_, c) => data.matrix.reduce((sum, row) => sum + row[c], 0));
    const grandTotal = rowTotals.reduce((a, b) => a + b, 0);
    return { rowTotals, colTotals, grandTotal };
  }, [data]);

  // ── Resize observer ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        });
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // ── Draw heatmap ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canvasRef.current || !data || !layout) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const cw = layout.canvasW;
    const ch = layout.canvasH;
    canvas.width = cw * dpr;
    canvas.height = ch * dpr;
    canvas.style.width = `${cw}px`;
    canvas.style.height = `${ch}px`;
    ctx.scale(dpr, dpr);

    const { leftMargin, bottomMargin, topMargin, cellW, cellH, rowCount, colCount, totalGap, totalColW, totalRowH } = layout;
    const textColor = isDark ? '#ddd' : '#444';
    const gridColor = isDark ? '#333' : '#e0e0e0';

    // Clear
    ctx.clearRect(0, 0, cw, ch);

    // Title — dynamic based on selected axes
    const axisLabelMap = Object.fromEntries((axisOptions || []).map(o => [o.value, o.label]));
    const yLabel = axisLabelMap[(axes && axes.y) || data.yAxis] || 'Rows';
    const xLabel = axisLabelMap[(axes && axes.x) || data.xAxis] || 'Columns';
    ctx.font = 'bold 14px "DIN 2014 ROUNDED REGULAR", "Segoe UI", sans-serif';
    ctx.fillStyle = isDark ? '#eee' : '#333';
    ctx.textAlign = 'center';
    ctx.fillText(`${yLabel} × ${xLabel} — ${recordLabelPlural}`, leftMargin + (colCount * cellW) / 2, 22);

    // Draw cells
    for (let r = 0; r < rowCount; r++) {
      for (let c = 0; c < colCount; c++) {
        const x = leftMargin + c * cellW;
        const y = topMargin + r * cellH;
        const val = data.matrix[r][c];

        // Cell fill
        ctx.fillStyle = interpolateColor(val, data.maxValue);
        ctx.fillRect(x, y, cellW - 1, cellH - 1);

        // Grid line
        ctx.strokeStyle = gridColor;
        ctx.lineWidth = 0.5;
        ctx.strokeRect(x, y, cellW - 1, cellH - 1);

        // Cell number if it fits
        if (val > 0 && cellW >= 28 && cellH >= 18) {
          ctx.font = '11px "Segoe UI", sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          // Choose readable text colour based on cell intensity
          const t = data.maxValue > 0 ? val / data.maxValue : 0;
          ctx.fillStyle = t > 0.55 ? '#fff' : (isDark ? '#ccc' : '#333');
          ctx.fillText(String(val), x + cellW / 2 - 0.5, y + cellH / 2);
        }
      }
    }

    // Row labels (Industry — y-axis)
    ctx.font = '11px "Segoe UI", sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = textColor;
    for (let r = 0; r < rowCount; r++) {
      const y = topMargin + r * cellH + cellH / 2;
      let label = clipTopicName(data.rows[r]);
      // Truncate long labels
      const maxChars = Math.floor((leftMargin - 8) / 6);
      if (label.length > maxChars) label = label.substring(0, maxChars - 1) + '…';
      ctx.fillText(label, leftMargin - 6, y);
    }

    // ── Totals row & column ────────────────────────────────────────────────
    if (totals) {
      const totalBg = isDark ? 'rgba(60,55,50,0.8)' : 'rgba(235,230,225,0.9)';
      const totalText = isDark ? '#eee' : '#333';

      // Row totals – extra column to the right
      const tx = leftMargin + colCount * cellW + totalGap;
      // "Total" column header
      ctx.save();
      ctx.font = 'bold 10px "Segoe UI", sans-serif';
      ctx.fillStyle = textColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText('Total', tx + totalColW / 2, topMargin - 4);
      ctx.restore();

      ctx.font = 'bold 11px "Segoe UI", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (let r = 0; r < rowCount; r++) {
        const x = tx;
        const y = topMargin + r * cellH;
        ctx.fillStyle = totalBg;
        ctx.fillRect(x, y, totalColW - 1, cellH - 1);
        ctx.strokeStyle = gridColor;
        ctx.lineWidth = 0.5;
        ctx.strokeRect(x, y, totalColW - 1, cellH - 1);
        ctx.fillStyle = totalText;
        ctx.fillText(String(totals.rowTotals[r]), x + totalColW / 2, y + cellH / 2);
      }

      // Column totals – extra row below the matrix
      const ty = topMargin + rowCount * cellH + totalGap;
      // "Total" row label
      ctx.font = 'bold 11px "Segoe UI", sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = textColor;
      ctx.fillText('Total', leftMargin - 6, ty + totalRowH / 2);

      ctx.textAlign = 'center';
      for (let c = 0; c < colCount; c++) {
        const x = leftMargin + c * cellW;
        const y = ty;
        ctx.fillStyle = totalBg;
        ctx.fillRect(x, y, cellW - 1, totalRowH - 1);
        ctx.strokeStyle = gridColor;
        ctx.lineWidth = 0.5;
        ctx.strokeRect(x, y, cellW - 1, totalRowH - 1);
        ctx.fillStyle = totalText;
        ctx.font = 'bold 11px "Segoe UI", sans-serif';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(totals.colTotals[c]), x + cellW / 2, y + totalRowH / 2);
      }

      // Grand total (bottom-right intersection)
      ctx.fillStyle = isDark ? 'rgba(80,70,60,0.9)' : 'rgba(220,215,210,0.95)';
      ctx.fillRect(tx, ty, totalColW - 1, totalRowH - 1);
      ctx.strokeStyle = gridColor;
      ctx.lineWidth = 0.5;
      ctx.strokeRect(tx, ty, totalColW - 1, totalRowH - 1);
      ctx.fillStyle = totalText;
      ctx.font = 'bold 12px "Segoe UI", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(totals.grandTotal), tx + totalColW / 2, ty + totalRowH / 2);
    }

    // Column labels (Topics — x-axis, rotated)
    ctx.save();
    ctx.font = '11px "Segoe UI", sans-serif';
    ctx.fillStyle = textColor;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    for (let c = 0; c < colCount; c++) {
      const x = leftMargin + c * cellW + cellW / 2;
      const y = topMargin + rowCount * cellH + totalGap + totalRowH + 8;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(-Math.PI / 3); // -60°
      ctx.fillText(clipTopicName(data.columns[c]), 0, 0);
      ctx.restore();
    }
    ctx.restore();

    // Colour-bar legend on the right
    const barX = leftMargin + colCount * cellW + totalGap + totalColW + 16;
    const barY = topMargin;
    const barW = 14;
    const barH = Math.min(rowCount * cellH, 200);
    for (let i = 0; i < barH; i++) {
      const t = 1 - i / barH; // top = max, bottom = 0
      ctx.fillStyle = interpolateColor(t * data.maxValue, data.maxValue);
      ctx.fillRect(barX, barY + i, barW, 1);
    }
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 1;
    ctx.strokeRect(barX, barY, barW, barH);

    ctx.font = '10px "Segoe UI", sans-serif';
    ctx.fillStyle = textColor;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(String(data.maxValue), barX + barW + 4, barY);
    ctx.textBaseline = 'bottom';
    ctx.fillText('0', barX + barW + 4, barY + barH);

  }, [data, layout, totals, dimensions, interpolateColor, isDark, recordLabelPlural]);

  // ── Mouse-move tooltip ────────────────────────────────────────────────────
  const handleMouseMove = useCallback((e) => {
    if (!layout || !data || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    const { leftMargin, topMargin, cellW, cellH, rowCount, colCount, totalGap, totalColW, totalRowH } = layout;

    const col = Math.floor((mx - leftMargin) / cellW);
    const row = Math.floor((my - topMargin) / cellH);

    // Totals column / row hit areas
    const tcx = leftMargin + colCount * cellW + totalGap;
    const tcy = topMargin + rowCount * cellH + totalGap;
    const inTotalCol = mx >= tcx && mx < tcx + totalColW;
    const inTotalRow = my >= tcy && my < tcy + totalRowH;

    if (col >= 0 && col < colCount && row >= 0 && row < rowCount) {
      setHoveredCell({
        industry: data.rows[row],
        topic: data.columns[col],
        value: data.matrix[row][col],
        x: e.clientX,
        y: e.clientY
      });
    } else if (inTotalCol && row >= 0 && row < rowCount && totals) {
      setHoveredCell({
        industry: data.rows[row],
        topic: 'Total',
        value: totals.rowTotals[row],
        x: e.clientX,
        y: e.clientY
      });
    } else if (inTotalRow && col >= 0 && col < colCount && totals) {
      setHoveredCell({
        industry: 'Total',
        topic: data.columns[col],
        value: totals.colTotals[col],
        x: e.clientX,
        y: e.clientY
      });
    } else if (inTotalCol && inTotalRow && totals) {
      setHoveredCell({
        industry: 'Total',
        topic: 'Total',
        value: totals.grandTotal,
        x: e.clientX,
        y: e.clientY
      });
    } else {
      setHoveredCell(null);
    }
  }, [layout, data, totals]);

  const handleMouseLeave = useCallback(() => setHoveredCell(null), []);

  // Click on a real (non-total) cell triggers onCellClick with row/column/value.
  const handleCanvasClick = useCallback((e) => {
    if (!onCellClick || !layout || !data || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const { leftMargin, topMargin, cellW, cellH, rowCount, colCount } = layout;
    const col = Math.floor((mx - leftMargin) / cellW);
    const row = Math.floor((my - topMargin) / cellH);
    if (col >= 0 && col < colCount && row >= 0 && row < rowCount) {
      onCellClick({
        row: data.rows[row],
        column: data.columns[col],
        value: data.matrix[row][col],
      });
    }
  }, [onCellClick, layout, data]);

  // Reusable axis selector UI — collapsed to a small toggle button; expands on click
  const renderAxisControls = () => {
    if (!onAxisChange || !axisOptions || axisOptions.length < 2) return null;
    const cur = axes || { x: data?.xAxis, y: data?.yAxis };
    return (
      <div className="heatmap-axis-wrap" ref={axisMenuRef}>
        <button
          type="button"
          className={`heatmap-axis-toggle ${axisMenuOpen ? 'is-open' : ''}`}
          onClick={() => setAxisMenuOpen(v => !v)}
          aria-expanded={axisMenuOpen}
          aria-label="Choose heatmap axes"
          title="Choose heatmap axes"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="4" y1="20" x2="4" y2="4" />
            <line x1="4" y1="20" x2="20" y2="20" />
            <polyline points="8,14 12,10 16,14" />
          </svg>
          <span className="heatmap-axis-toggle-label">Axes</span>
        </button>
        {axisMenuOpen && (
          <div className="heatmap-axis-controls" role="group" aria-label="Heatmap axes">
            {[{ key: 'y', label: 'Y' }, { key: 'x', label: 'X' }].map(({ key, label }) => (
              <div key={key} className="heatmap-axis-group">
                <span className="heatmap-axis-label">{label}</span>
                <div className="heatmap-axis-pills">
                  {axisOptions.map(o => (
                    <button
                      key={o.value}
                      type="button"
                      className={`heatmap-axis-pill ${cur[key] === o.value ? 'is-active' : ''}`}
                      onClick={() => onAxisChange({ ...cur, [key]: o.value })}
                      title={`${label} axis: ${o.label}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── Empty state ───────────────────────────────────────────────────────────
  if (!data || !data.rows || data.rows.length === 0 || !data.columns || data.columns.length === 0) {
    return (
      <div className="heatmap-panel heatmap-empty" ref={containerRef}>
        {renderAxisControls()}
        <p>No heatmap data available for the current filters.</p>
        <p className="heatmap-empty-hint">
          Try changing the axes or relaxing the filters.
        </p>
      </div>
    );
  }

  return (
    <div className="heatmap-panel" ref={containerRef}>
      {renderAxisControls()}
      <canvas
        ref={canvasRef}
        className="heatmap-canvas"
        style={layout ? { width: layout.canvasW, height: layout.canvasH, cursor: onCellClick ? 'pointer' : 'default' } : undefined}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleCanvasClick}
      />
      {hoveredCell && (
        <div
          ref={tooltipRef}
          className="heatmap-tooltip"
          style={{
            left: hoveredCell.x + 14,
            top: hoveredCell.y - 40
          }}
        >
          <strong>{hoveredCell.industry}</strong> × <strong>{hoveredCell.topic}</strong>
          <br />
          {recordLabelPlural}: <strong>{hoveredCell.value}</strong>
        </div>
      )}
    </div>
  );
}

export default HeatmapPanel;
